import { useMutation, useQuery, type UseQueryOptions } from 'react-query'

import queryClient from '../../queryClient'
import { type ApiResponse, HTTPMethod, request } from '../api/legacyApi'

import { getLiveId } from './roomInfo'
import { getLiveToken } from './useConnectInfo'

type LiveDataDTO = {
  before_webinar_countdown_p: boolean
  broadcasting_p: boolean
  cancelled_p: boolean
  comment_mode: string
  question_mode: string
  draft_p: boolean
  token: string
  duration_minutes: string
  live_format: string
  live_id: string
  live_label: string
  live_status: string
  locale: string
  name: string
  number_of_comments: string
  number_of_comments_fmt: string
  primary_timezone: string
  primary_timezone_live_date__date: string
  primary_timezone_live_date__time: string
  template_p: boolean
  description_html: string
  allow_guest_speakers_p: boolean
  streaming_method: string
  live_series_id: string
  live_series_token: string
  live_date_epoch: number
  thumbnail_small_download: string
  description_text: string
  enable_ondemand_p: boolean
  after_webinar_link: string
  after_webinar_message: string
  after_webinar_headline: string
  after_webinar_photo_url: string
  after_webinar_photo_id: string
  after_webinar_photo_token: string
  before_webinar_photo_url: string
  before_webinar_photo_id: string
  before_webinar_photo_token: string
  webinar_design_id: number
}

const liveDataQuery = {
  queryKey: ['live_data'],
  queryFn: async () => {
    const liveId = getLiveId()
    const params = {
      live_id: `${liveId}`,
      include_stats_p: true,
      include_webinar_images_p: true,
      token: await getLiveToken(),
    }

    const response = await request<ApiResponse<LiveDataDTO[]>>({
      url: '/api/2/live/list',
      method: HTTPMethod.GET,
      data: params,
    })

    return response.data[0]
  },
  staleTime: Infinity,
  cacheTime: Infinity,
  refetchInterval: (liveData) => (liveData?.broadcasting_p ? 5000 : 2000),
} satisfies UseQueryOptions<LiveDataDTO>

const fetchLiveData = async () => {
  return queryClient.fetchQuery(liveDataQuery)
}

const useLiveDataQuery = () => {
  return useQuery(liveDataQuery)
}

const useUpdateLiveDataMutation = () => {
  return useMutation({
    mutationFn: async (data: Partial<LiveDataDTO>) => {
      const liveId = getLiveId()
      const liveToken = await getLiveToken()

      const response = await request<ApiResponse<LiveDataDTO>>({
        url: '/api/2/live/update',
        method: HTTPMethod.POST,
        data: {
          live_id: liveId.toString(),
          token: liveToken,
          live_format: 'webinar',
          ...data,
        },
      })

      return response.data
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries(['live_data'])
      const previousData = queryClient.getQueryData<LiveDataDTO>(['live_data'])

      if (previousData) {
        queryClient.setQueryData(['live_data'], {
          ...previousData,
          ...data,
        })
      }

      return { previousData }
    },
    onError: (_error, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(['live_data'], context.previousData)
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries(['live_data'])
    },
  })
}

const useUpdateOnCameraState = () => {
  const { mutate: update } = useUpdateLiveDataMutation()

  return (allowOnCamera: boolean) =>
    update({ allow_guest_speakers_p: allowOnCamera })
}

const useUpdateQuestionMode = () => {
  const { mutate: update } = useUpdateLiveDataMutation()

  return (isQuestionsEnabled: boolean) =>
    update({ question_mode: isQuestionsEnabled ? 'ask' : 'none' })
}

export {
  useLiveDataQuery,
  fetchLiveData,
  useUpdateLiveDataMutation,
  useUpdateOnCameraState,
  useUpdateQuestionMode,
}
